import React from "react";
import { motion } from "framer-motion";
import { FaGraduationCap } from "react-icons/fa";

const experiences = [
  {
    company: "Pathlock",
    role: "Software Development Engineer - I",
    period: "Jun 2025 — Present",
    location: "Chandigarh",
    year: "'25",
    logo: "https://pathlock.com/wp-content/uploads/2026/03/logo_pathlock.svg",
    stack: ["C#", ".NET Core", "EF Core", "RabbitMQ", "SQL Server"],
    bullets: [
      "Architected scalable backend services with EF Core — orchestrating data materialization pipelines and approval workflows across 4,500+ lines of production code.",
      "Built event-driven worker services with message queues, asynchronously processing routing logic and executing direct identity provisioning in SAP ERP.",
      "Engineered thread-safe data sync pipelines with MERGE + HOLDLOCK, ensuring ACID compliance across high-throughput concurrent transactions.",
      "Deployed fail-safe telemetry logging across cross-functional teams, significantly improving system observability.",
    ],
  },
  {
    company: "Advantage Club",
    role: "Software Engineer Intern",
    period: "Jan 2024 — Jul 2024",
    location: "Gurgaon",
    year: "'24",
    logo: "https://cdn3.workadvantage.in/images/cdn_upload/cdn/18921/a358e57975.webp",
    stack: ["React", "Redux Saga", "CryptoJS", "REST APIs"],
    bullets: [
      "Built atomic budget transfer workflows with Redux Saga — decoupled side effects, reduced manual operational overhead by 30%.",
      "Secured real-time financial transaction payloads for Kotak Bank via Client-Side Encryption (CryptoJS).",
    ],
  },
];

const achievements = [
  { title: "LeetCode Knight", detail: "Top 5% globally — 1,000+ problems, 1950+ rating", icon: "LC" },
  { title: "Google Kickstart", detail: "Global Rank 8179 / Round G / 10K+ participants", icon: "GK" },
  { title: "GCP Legend", detail: "Legend Tier — scalable cloud infra on Google Cloud", icon: "GC" },
  { title: "Tech Writer", detail: "CAP Theorem, API Versioning, N+1 bottlenecks", icon: "TW" },
];

const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.1 } } };
const fadeUp = { hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] } } };

const Experience = () => {
  return (
    <div className="relative text-white max-w-5xl mx-auto px-6 py-16 overflow-hidden">
      <div className="noise-overlay"></div>

      {/* Ambient glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-blue-600/8 rounded-full blur-[120px] animate-drift"></div>
        <div className="absolute bottom-0 -left-40 w-[400px] h-[400px] bg-violet-600/6 rounded-full blur-[120px] animate-drift-reverse"></div>
      </div>

      {/* Section header */}
      <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="relative z-10">
        <p className="text-[10px] text-gray-600 uppercase tracking-[0.4em] font-mono mb-2">01 / Experience</p>
        <h2 className="text-4xl md:text-5xl font-black tracking-tight">Where I've<br /><span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-violet-500">shipped code</span></h2>
      </motion.div>

      {/* Experience cards */}
      <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} className="relative z-10 mt-16 space-y-0">
        {experiences.map((exp, i) => (
          <motion.div key={i} variants={fadeUp} className="group relative grid grid-cols-12 gap-4 py-10 border-t border-gray-800/60 hover:border-gray-600/60 transition-colors hover:bg-white/[0.01] rounded-lg">
            {/* Year marker */}
            <div className="col-span-2 md:col-span-1">
              <span className="text-4xl md:text-5xl font-black text-gray-800/80 group-hover:text-blue-400/40 transition-colors duration-700 select-none">{exp.year}</span>
            </div>

            {/* Content */}
            <div className="col-span-10 md:col-span-11">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-5">
                <div className="flex items-start gap-4">
                  <img src={exp.logo} alt={exp.company} className="w-10 h-10 object-contain rounded-lg bg-white/5 p-1.5 border border-gray-700/50 group-hover:border-blue-500/30 flex-shrink-0 mt-0.5 transition-colors duration-500" />
                  <div>
                    <h3 className="text-xl font-bold text-white group-hover:text-blue-300 transition-colors duration-300">{exp.role}</h3>
                    <p className="text-sm text-gray-500">{exp.company} &middot; {exp.location}</p>
                  </div>
                </div>
                <span className="text-xs font-mono text-gray-600 bg-gray-800/40 px-3 py-1 rounded-full flex-shrink-0 self-start">{exp.period}</span>
              </div>

              <ul className="space-y-3 mb-5">
                {exp.bullets.map((b, j) => (
                  <li key={j} className="text-sm text-gray-400 leading-relaxed pl-4 relative before:content-[''] before:absolute before:left-0 before:top-[9px] before:w-1.5 before:h-1.5 before:rounded-full before:bg-gray-700 group-hover:before:bg-blue-500/50 before:transition-colors before:duration-500">
                    {b}
                  </li>
                ))}
              </ul>

              <div className="flex flex-wrap gap-2">
                {exp.stack.map((t, j) => (
                  <span key={j} className="px-2.5 py-1 text-[10px] font-mono text-gray-500 bg-blue-500/5 border border-blue-500/10 rounded-full group-hover:text-blue-400 group-hover:bg-blue-500/10 group-hover:border-blue-500/25 transition-all duration-500">{t}</span>
                ))}
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Education */}
      <motion.div className="mt-20" initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
        <p className="text-[10px] text-gray-600 uppercase tracking-[0.4em] font-mono mb-2">02 / Education</p>
        <h2 className="text-4xl md:text-5xl font-black tracking-tight mb-12">The <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-400">foundation</span></h2>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
        className="group grid grid-cols-12 gap-4 py-8 border-t border-gray-800/60">
        <div className="col-span-2 md:col-span-1">
          <span className="text-4xl md:text-5xl font-black text-gray-800/80 group-hover:text-purple-500/30 transition-colors duration-700 select-none">'25</span>
        </div>
        <div className="col-span-10 md:col-span-11 flex items-start gap-4">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500/20 to-blue-500/10 flex items-center justify-center flex-shrink-0 mt-0.5 border border-gray-800/50">
            <FaGraduationCap className="text-purple-400" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-white">Punjab Engineering College (PEC)</h3>
            <p className="text-sm text-gray-500 mt-0.5">B.Tech in Electrical Engineering &middot; Chandigarh &middot; 2021 — 2025</p>
          </div>
        </div>
      </motion.div>

      {/* Achievements */}
      <motion.div className="mt-20" initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
        <p className="text-[10px] text-gray-600 uppercase tracking-[0.4em] font-mono mb-2">03 / Achievements</p>
        <h2 className="text-4xl md:text-5xl font-black tracking-tight mb-12">Beyond <span className="bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 to-orange-400">the code</span></h2>
      </motion.div>

      <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-px bg-gray-800/30 rounded-xl overflow-hidden">
        {achievements.map((a, i) => {
          const iconColors = ["group-hover:text-yellow-400 group-hover:border-yellow-500/30 group-hover:bg-yellow-500/10", "group-hover:text-blue-400 group-hover:border-blue-500/30 group-hover:bg-blue-500/10", "group-hover:text-emerald-400 group-hover:border-emerald-500/30 group-hover:bg-emerald-500/10", "group-hover:text-purple-400 group-hover:border-purple-500/30 group-hover:bg-purple-500/10"];
          return (
          <motion.div key={i} variants={fadeUp} className="group bg-[#0a0a2e]/60 backdrop-blur-sm p-6 hover:bg-white/[0.03] transition-all duration-500">
            <div className="flex items-start gap-4">
              <div className={`w-10 h-10 rounded-lg bg-gray-800/60 border border-gray-700/50 flex items-center justify-center flex-shrink-0 text-xs font-black font-mono text-gray-500 ${iconColors[i]} transition-all duration-500`}>
                {a.icon}
              </div>
              <div>
                <h3 className="font-bold text-white text-sm group-hover:text-gray-100">{a.title}</h3>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">{a.detail}</p>
              </div>
            </div>
          </motion.div>
          );
        })}
      </motion.div>
    </div>
  );
};

export default Experience;
